import type { UserCreateSchema } from '@repo/schemas/user/userCreate';
import type { UserEditSchema } from '@repo/schemas/user/userEdit';

import { useFormContext } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneNumberInput } from '@/components/ui/input/phone-number';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCredentials } from '@/store/auth-store';

interface UserFormFieldsProps {
  hideLvl?: boolean;
  /**
   * Appended to the field placeholders (e.g. 'rodzica' -> 'Imię rodzica') so
   * nested forms stay distinguishable from the surrounding user form.
   */
  placeholderSuffix?: string;
}

export function UserFormFields({ hideLvl = false, placeholderSuffix }: UserFormFieldsProps = {}) {
  const { control } = useFormContext<UserCreateSchema | UserEditSchema>();
  const { lvl } = useCredentials();
  const placeholder = (label: string) => (placeholderSuffix ? `${label} ${placeholderSuffix}` : label);

  return (
    <>
      <FormField
        name="name"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Imię</FormLabel>
            <FormControl>
              <Input {...field} placeholder={placeholder('Imię')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="surname"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nazwisko</FormLabel>
            <FormControl>
              <Input {...field} placeholder={placeholder('Nazwisko')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="email"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input {...field} placeholder={placeholder('Email')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {!hideLvl && (
        <FormField
          name="lvl"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Poświadczenia</FormLabel>
              <Select onValueChange={(value) => field.onChange(Number(value))} value={String(field.value)}>
                <FormControl>
                  <SelectTrigger className="ps-2 [&>span]:flex [&>span]:items-center [&>span]:gap-2 [&>span_[data-square]]:shrink-0">
                    <SelectValue placeholder={'Poświadczenia'} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="[&_*[role=option]]:ps-2 [&_*[role=option]]:pe-8 [&_*[role=option]>span]:start-auto [&_*[role=option]>span]:end-2 [&_*[role=option]>span]:flex [&_*[role=option]>span]:items-center [&_*[role=option]>span]:gap-2">
                  <SelectGroup>
                    <SelectItem value={'0'}>
                      <Badge variant={'blue'}>U</Badge>
                      <span className={'truncate'}>Uczeń</span>
                    </SelectItem>
                    <SelectItem value={'1'}>
                      <Badge variant={'turquoise'}>N</Badge>
                      <span className={'truncate'}>Nauczyciel</span>
                    </SelectItem>
                    <SelectItem disabled={lvl != 2} value={'2'}>
                      <Badge variant={'purple'}>A</Badge>
                      <span className={'truncate'}>Administrator</span>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        name="phone"
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Numer telefonu <small className={'text-text-tertiary text-sm'}>(opcionalne)</small>
            </FormLabel>
            <FormControl>
              <PhoneNumberInput
                {...field}
                onChange={(value) => {
                  field.onChange(value ? value : null);
                }}
                value={field.value ? field.value : undefined}
                placeholder={placeholder('Numer telefonu')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="birthdate"
        control={control}
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>
                Data urodzenia <small className={'text-text-tertiary text-sm'}>(opcionalne)</small>
              </FormLabel>
              <FormControl>
                <DatePicker
                  {...field}
                  matcher={{ after: new Date() }}
                  selected={field.value ? new Date(field.value) : null}
                  onSelect={(date) => {
                    if (!date) {
                      field.onChange(null);
                      return;
                    }
                    field.onChange(date.toISOString());
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </>
  );
}
